import React, { memo, useEffect, useState } from "react";
import { Button, Empty, Table, Tag } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { AuthStorage } from "utils/auth-storage";
import { ActiveUsersService } from "apis/Services/ActiveUsersService";
import LoadingComponent from "components/loading-component";
import {
  getProjectLink,
  ProjectAlerts,
  ProjectIdentity,
  ProjectProgress,
} from "./project-row";
import styles from "./table.module.css";

const ProjectTable = ({
  data = [],
  companyId,
  loading,
  isFiltered,
  onClearFilters,
}) => {
  const navigate = useNavigate();
  const [userAccessLevels, setUserAccessLevels] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setPage(1);
  }, [data]);

  useEffect(() => {
    let active = true;
    setUserAccessLevels(null);
    ActiveUsersService.UserInfo({
      company_id: companyId,
      loginEmail: AuthStorage.getItem("email"),
      code: AuthStorage.getItem("token"),
    })
      .then((response) => {
        if (active) setUserAccessLevels(response);
      })
      .catch((error) => {
        console.error("Error fetching user access levels:", error);
      });
    return () => {
      active = false;
    };
  }, [companyId]);

  const projectLink = (project) =>
    getProjectLink(project, companyId, userAccessLevels);
  const columns = [
    {
      title: "Project",
      key: "project",
      width: 240,
      render: (_, project) => (
        <ProjectIdentity project={project} href={projectLink(project)} />
      ),
    },
    {
      title: "Project type",
      dataIndex: "Type",
      width: 110,
      render: (value) => value || <span className={styles.secondary}>—</span>,
    },
    {
      title: "Start date",
      dataIndex: "actualStartDate",
      width: 105,
      render: (value) => <span className={styles.date}>{value || "—"}</span>,
    },
    {
      title: "End date",
      dataIndex: "completionDate",
      width: 105,
      render: (value) => <span className={styles.date}>{value || "—"}</span>,
    },
    {
      title: "Progress",
      dataIndex: "Progress",
      width: 135,
      render: (value) => <ProjectProgress value={value} />,
    },
    {
      title: "Registration status",
      dataIndex: "RegistrationStatus",
      width: 150,
      render: (value) =>
        value ? (
          <Tag
            className={styles.status}
            color={value === "Active" ? "success" : value === "Inactive" ? "default" : "error"}
          >
            {value}
          </Tag>
        ) : (
          <span className={styles.secondary}>—</span>
        ),
    },
    {
      title: "Alerts",
      key: "alerts",
      width: 230,
      render: (_, project) => <ProjectAlerts project={project} />,
    },
    {
      title: <span className={styles.srOnly}>Open project</span>,
      key: "open",
      width: 40,
      render: (_, project) => (
        <Link
          className={styles.openProject}
          to={projectLink(project)}
          aria-label={`Open ${project["Project Name"] || "project"}`}
        >
          <ArrowRightOutlined />
        </Link>
      ),
    },
  ];

  return (
    <Table
      className={styles.table}
      columns={columns}
      dataSource={data}
      rowKey={(project) =>
        project["Project ID"] ?? project["Project Number"] ?? project["Project Name"]
      }
      size="middle"
      loading={{ spinning: Boolean(loading), indicator: <LoadingComponent /> }}
      scroll={{ x: 1215 }}
      pagination={{
        current: page,
        pageSize,
        showSizeChanger: true,
        hideOnSinglePage: true,
        onChange: (nextPage, nextPageSize) => {
          setPage(nextPageSize === pageSize ? nextPage : 1);
          setPageSize(nextPageSize);
        },
      }}
      onRow={(project) => ({
        className: styles.projectRow,
        onClick: (event) => {
          if (event.target.closest("a, button")) return;
          navigate(projectLink(project));
        },
      })}
      locale={{
        emptyText: loading ? (
          "Loading projects…"
        ) : (
          <Empty
            className={styles.emptyState}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div className={styles.emptyDescription}>
                <strong>
                  {isFiltered
                    ? "No projects match your search or filters"
                    : "No projects available"}
                </strong>
                <span className={styles.secondary}>
                  {isFiltered
                    ? "Try another search or adjust your filters."
                    : "Projects you have access to will appear here. You can also select another company."}
                </span>
              </div>
            }
          >
            {isFiltered && (
              <Button onClick={onClearFilters}>Clear search and filters</Button>
            )}
          </Empty>
        ),
      }}
    />
  );
};

export default memo(ProjectTable);
